import { useState } from "react";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import {
  ArtifactRefreshRun,
  getPinnedArtifact,
  isRunLive,
  projectPinnedArtifactsPath,
  refreshPinnedArtifact,
  refreshRunPath,
  pinnedArtifactPreviewPath,
} from "~/services/pinnedArtifacts";
import { ApiError } from "~/utils/apiClient";

const POLL_INTERVAL_MS = 2000;

export interface UseArtifactRefreshRun {
  run: ArtifactRefreshRun | undefined;
  isStarting: boolean;
  start: () => Promise<void>;
}

/** The runs a pin knows about: its open one, if any, and its last success. */
export interface PinRuns {
  activeRunId: string | null;
  lastRunId: string | null;
}

/** Starts a refresh and follows it to a terminal status.
 *
 * Which run is followed is decided on every render, because the drawer stays
 * mounted while the pin list revalidates underneath it:
 *
 *   pin's open run  ??  the run this tab started  ??  pin's last success
 *
 * The open run comes first so a refresh another member starts is followed as
 * soon as the pin names it. The run this tab started covers the moments before
 * the pin knows about it, and after it ends.
 *
 * A pin runs one refresh at a time, guarded by an advisory lock in the
 * analysis-agent. If another refresh holds it, starting answers 409 and this
 * follows that run instead.
 *
 * Polling stops as soon as the run reaches a terminal status: SWR takes a
 * function for `refreshInterval` and treats 0 as "stop", so the run's own state
 * ends the loop without an effect watching it.
 */
export const useArtifactRefreshRun = (
  projectId: string,
  pinId: string,
  { activeRunId, lastRunId }: PinRuns
): UseArtifactRefreshRun => {
  const { showError } = useToast();
  const [startedRunId, setStartedRunId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const runId = activeRunId ?? startedRunId ?? lastRunId;

  const { data: run } = useFetcher<ArtifactRefreshRun>(
    runId ? refreshRunPath(projectId, pinId, runId) : null,
    {
      refreshInterval: (latest) => (isRunLive(latest) ? POLL_INTERVAL_MS : 0),
      onSuccess: (latest) => {
        if (latest.status !== "succeeded") return;
        // The pin now points at a newly produced artifact, so both the list and
        // this card's preview are stale. Revalidating the preview is what makes
        // the card show the refreshed result without a reload.
        void mutate(projectPinnedArtifactsPath(projectId));
        void mutate(pinnedArtifactPreviewPath(projectId, pinId));
      },
    }
  );

  // Read the pin for the run that beat this one, rather than parsing the 409:
  // the pin is the record of which run is open. If it no longer names one, that
  // run ended in between, and the pin's last run may be an older success, so
  // this says what happened and refetches the pins rather than showing that.
  const followRunInProgress = async () => {
    let pin;
    try {
      pin = await getPinnedArtifact(projectId, pinId);
    } catch (e) {
      console.error("Couldn't read the pin after a refresh conflict", e);
      showError(e instanceof Error && e.message ? e.message : "Couldn't read this artifact");
      return;
    }
    if (pin.active_run_id) {
      setStartedRunId(pin.active_run_id);
      void mutate(projectPinnedArtifactsPath(projectId));
      return;
    }
    void mutate(projectPinnedArtifactsPath(projectId));
    showError("Another refresh of this artifact just ended. Check the result, then try again.");
  };

  const start = async () => {
    setIsStarting(true);
    try {
      const started = await refreshPinnedArtifact(projectId, pinId);
      setStartedRunId(started.id);
      // The cached list may still name an older run as open, a lost one, and the
      // pin's open run wins over this one, so refetch it to name this run.
      void mutate(projectPinnedArtifactsPath(projectId));
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        await followRunInProgress();
        return;
      }
      // A workflow that cannot be re-run at all answers with the reason,
      // which is more useful to show than a generic failure.
      showError(e instanceof Error && e.message ? e.message : "Couldn't refresh this artifact");
    } finally {
      setIsStarting(false);
    }
  };

  return { run, isStarting, start };
};
